'use client';
import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cn } from '@/app/lib/utils';
import { Check, Minus } from 'lucide-react';

export type CheckboxProps = React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
  wrapperClassNames?: string;
  intermediate?: boolean;
};

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, children, id, wrapperClassNames, intermediate, ...props }, ref) => {
    const randomId = React.useId();
    const idValue = React.useMemo(() => {
      return id ?? randomId;
    }, [id]);

    return (
      <div className={cn('flex items-center gap-3', wrapperClassNames)}>
        <CheckboxPrimitive.Root
          ref={ref}
          id={idValue}
          className={cn(
            'grid place-content-center peer h-4 w-4 shrink-0 rounded-md border border-border ring-offset-background transition-all duration-200 ease-in-out active:scale-95 cursor-pointer hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100 data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:bg-primary data-[state=indeterminate]:border-primary data-[state=indeterminate]:text-primary-foreground',
            className
          )}
          {...props}
        >
          <CheckboxPrimitive.Indicator
            forceMount
            className={cn(
              'grid place-content-center text-current transition-all duration-200 ease-in-out data-[state=unchecked]:scale-0 data-[state=unchecked]:opacity-0 data-[state=checked]:scale-100 data-[state=checked]:opacity-100 data-[state=indeterminate]:scale-100 data-[state=indeterminate]:opacity-100'
            )}
          >
            {intermediate || props.checked === 'indeterminate' ? (
              <Minus className="h-4 w-4" />
            ) : (
              <Check className="h-4 w-4" />
            )}
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
        {children && (
          <label className="cursor-pointer text-sm text-foreground select-none" htmlFor={idValue}>
            {children}
          </label>
        )}
      </div>
    );
  }
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
export default Checkbox;
